import { useEffect, useRef, useState } from 'react';

/**
 * Confirmar un borrado, encima de lo que estabas mirando.
 *
 * Antes esto se dibujaba **dentro** de la página, donde cayera. Con una lista
 * de setenta y nueve clientes eso significaba tocar Eliminar abajo de todo y que
 * la pregunta apareciera arriba de todo: había que volver a subir para
 * contestarla, encontrarla entre el resto, y bajar de nuevo. En un teléfono eso
 * no es una molestia, es perder el hilo de lo que estabas haciendo.
 *
 * Ahora es una hoja fija sobre la pantalla. Al ser `fixed` no participa del
 * flujo del documento, así que **el scroll no se mueve**: cuando se cierra, la
 * lista está exactamente donde estaba, con la misma fila bajo el pulgar.
 *
 * Va abajo y no al centro porque es donde llega el pulgar. Y las acciones
 * quedan en el mismo lugar siempre, para que la de cancelar no se aprenda por
 * casualidad.
 *
 * **Escribir la palabra sólo cuando lo que se borra tiene peso económico.** Un
 * trabajo facturado o un ajuste merecen esa fricción; un cliente cargado por
 * error, no —y pedirla en todo enseña a copiar sin leer, que es justamente lo
 * que la fricción quería evitar—.
 */
export function ConfirmarBorrado({
  que,
  detalle,
  palabra,
  ocupado = false,
  onCancelar,
  onConfirmar,
  testId,
}: {
  /** Qué se borra, con su nombre: «el ajuste Combustible». */
  que: string;
  /** Qué se lleva puesto, si se lleva algo. */
  detalle?: string | undefined;
  /** Pedir que se escriba esta palabra. Sin ella, alcanza con confirmar. */
  palabra?: string | undefined;
  ocupado?: boolean;
  onCancelar: () => void;
  onConfirmar: () => void;
  testId?: string | undefined;
}) {
  const [texto, setTexto] = useState('');
  const panel = useRef<HTMLDivElement>(null);

  // Sin distinguir mayúsculas ni espacios de más: lo que se comprueba es que
  // haya leído, no que sepa escribir con una mano y el teléfono en la otra.
  const habilitado = !palabra || texto.trim().toUpperCase() === palabra.toUpperCase();

  // Atrás y Escape cancelan, que es lo que espera cualquiera. Sin esto, en
  // Android el botón físico se lleva puesta la pantalla entera en vez de cerrar
  // la pregunta.
  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancelar();
    };
    document.addEventListener('keydown', alTeclado);
    return () => document.removeEventListener('keydown', alTeclado);
  }, [onCancelar]);

  useEffect(() => {
    panel.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      // Tocar afuera cancela. Es la salida que se prueba primero cuando algo
      // aparece sin que uno lo esperara.
      onClick={onCancelar}
      data-testid={testId ?? 'confirmar-borrado'}
    >
      <div
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        aria-label={`Confirmar: borrar ${que}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="zona-inferior margen-lateral-seguro w-full max-w-2xl rounded-t-2xl border-t border-[var(--linea)] bg-[var(--superficie)] p-4 shadow-2xl outline-none"
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--linea-fuerte)]" aria-hidden />

        <p className="text-base font-medium">¿Querés borrar {que}?</p>
        {detalle && <p className="mt-1 text-sm text-[var(--tinta-suave)]">{detalle}</p>}
        <p className="mt-1 text-sm text-[var(--tinta-suave)]">Esto no se puede deshacer.</p>

        {palabra && (
          <label className="mt-3 flex flex-col gap-1 text-sm">
            Escribí <strong>{palabra}</strong> para confirmar
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
              data-testid="confirmar-palabra"
            />
          </label>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onCancelar}
            className="min-h-12 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] font-medium"
            data-testid="confirmar-cancelar"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={!habilitado || ocupado}
            className="min-h-12 flex-1 rounded-[var(--radius-control)] bg-[var(--peligro-solido)] font-medium text-[var(--sobre-peligro)] disabled:opacity-40"
            data-testid="confirmar-borrar"
          >
            {ocupado ? 'Borrando…' : 'Borrar'}
          </button>
        </div>
      </div>
    </div>
  );
}
